import React, { useState } from 'react';
import { FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import Modal from '../UI/Modal';
import { useUser } from '../../contexts/UserContext';
import { chatApi } from '../../services/api';
import { GranularFeedbackItem } from '../../types';
import { FEEDBACK_QUESTIONS, FeedbackQuestion } from '../../config/feedbackConfig';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialRating: boolean;
  jobId: string;
  conversationId?: string;
  hasVisualization: boolean;
  hasSearchEnrichment: boolean;
  onSubmitted: (rating: boolean) => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  initialRating,
  jobId,
  conversationId,
  hasVisualization,
  hasSearchEnrichment,
  onSubmitted,
}) => {
  const { userEmail } = useUser();
  const [overallRating, setOverallRating] = useState<boolean>(initialRating);
  const [overallComment, setOverallComment] = useState('');
  const [granular, setGranular] = useState<Record<string, GranularFeedbackItem>>({});
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal opens with a new rating
  React.useEffect(() => {
    if (isOpen) {
      setOverallRating(initialRating);
      setOverallComment('');
      setGranular({});
      setExpandedKeys(new Set());
    }
  }, [isOpen, initialRating]);

  const visibleQuestions = FEEDBACK_QUESTIONS.filter((q: FeedbackQuestion) => {
    if (q.visibleWhen === 'always') return true;
    if (q.visibleWhen === 'hasVisualization') return hasVisualization;
    if (q.visibleWhen === 'hasSearchEnrichment') return hasSearchEnrichment;
    return false;
  });

  const handleGranularRating = (key: string, rating: boolean) => {
    const current = granular[key];
    const isSameRating = current?.rating === rating;

    if (isSameRating) {
      // Toggle off
      const updated = { ...granular };
      delete updated[key];
      setGranular(updated);
      setExpandedKeys(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    } else {
      setGranular(prev => ({
        ...prev,
        [key]: { ...prev[key], rating },
      }));
      setExpandedKeys(prev => new Set(prev).add(key));
    }
  };

  const handleGranularComment = (key: string, comment: string) => {
    setGranular(prev => ({
      ...prev,
      [key]: { ...prev[key], rating: prev[key]?.rating ?? null, comment },
    }));
  };

  const handleSubmit = async () => {
    if (!userEmail) return;
    setIsSubmitting(true);

    try {
      // Only include granular items that were actually rated
      const filteredGranular: Record<string, GranularFeedbackItem> = {};
      for (const [key, item] of Object.entries(granular)) {
        if (item.rating !== null && item.rating !== undefined) {
          filteredGranular[key] = item;
        }
      }

      await chatApi.submitFeedback({
        job_id: jobId,
        conversation_id: conversationId,
        user_email: userEmail,
        overall_rating: overallRating,
        overall_comment: overallComment || undefined,
        granular_feedback: Object.keys(filteredGranular).length > 0 ? filteredGranular : undefined,
      });

      onSubmitted(overallRating);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Share your feedback" size="lg">
      <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">

        {/* Tier 1: Overall rating */}
        <div>
          <p className="text-sm font-medium text-text-primary dark:text-dark-text mb-2">
            Overall, how was this response?
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setOverallRating(true)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all duration-200 text-sm font-medium ${
                overallRating === true
                  ? 'border-green-400 bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 dark:border-green-600'
                  : 'border-neutral-dark dark:border-dark-border text-text-secondary dark:text-dark-textSecondary hover:border-green-300 hover:text-green-600'
              }`}
            >
              <FiThumbsUp size={16} />
              Good
            </button>
            <button
              onClick={() => setOverallRating(false)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all duration-200 text-sm font-medium ${
                overallRating === false
                  ? 'border-red-400 bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 dark:border-red-600'
                  : 'border-neutral-dark dark:border-dark-border text-text-secondary dark:text-dark-textSecondary hover:border-red-300 hover:text-red-600'
              }`}
            >
              <FiThumbsDown size={16} />
              Needs improvement
            </button>
          </div>
          <textarea
            value={overallComment}
            onChange={(e) => setOverallComment(e.target.value)}
            placeholder="Any additional thoughts? (optional)"
            rows={2}
            className="mt-3 w-full px-3 py-2 text-sm rounded-xl border border-neutral-dark dark:border-dark-border bg-white dark:bg-dark-primary text-text-primary dark:text-dark-text placeholder-text-tertiary dark:placeholder-dark-textSecondary focus:outline-none focus:ring-1 focus:ring-primary resize-none"
          />
        </div>

        {/* Tier 2: Granular feedback */}
        {visibleQuestions.length > 0 && (
          <div>
            <p className="text-xs font-medium text-text-tertiary dark:text-dark-textSecondary uppercase tracking-wider mb-3">
              Help us improve specific areas
            </p>
            <div className="space-y-1">
              {visibleQuestions.map((q: FeedbackQuestion) => {
                const item = granular[q.key];
                const isExpanded = expandedKeys.has(q.key);

                return (
                  <div key={q.key}>
                    <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-neutral/50 dark:hover:bg-dark-accent/30 transition-colors">
                      <span className="text-sm text-text-primary dark:text-dark-text flex-1 mr-3">
                        {q.label}
                      </span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => handleGranularRating(q.key, true)}
                          className={`p-1 rounded-md transition-all duration-150 ${
                            item?.rating === true
                              ? 'text-green-500 bg-green-50 dark:bg-green-900/20'
                              : 'text-text-tertiary/60 dark:text-dark-textSecondary/60 hover:text-green-500'
                          }`}
                          aria-label={`${q.label} - good`}
                        >
                          <FiThumbsUp size={13} />
                        </button>
                        <button
                          onClick={() => handleGranularRating(q.key, false)}
                          className={`p-1 rounded-md transition-all duration-150 ${
                            item?.rating === false
                              ? 'text-red-500 bg-red-50 dark:bg-red-900/20'
                              : 'text-text-tertiary/60 dark:text-dark-textSecondary/60 hover:text-red-500'
                          }`}
                          aria-label={`${q.label} - needs improvement`}
                        >
                          <FiThumbsDown size={13} />
                        </button>
                      </div>
                    </div>
                    {/* Expandable comment area */}
                    <div
                      className={`overflow-hidden transition-all duration-200 ease-in-out ${
                        isExpanded ? 'max-h-24 opacity-100 mt-1 mb-2' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <textarea
                        value={item?.comment || ''}
                        onChange={(e) => handleGranularComment(q.key, e.target.value)}
                        placeholder="Add a comment (optional)"
                        rows={1}
                        className="w-full px-3 py-1.5 ml-3 text-xs rounded-lg border border-neutral-dark dark:border-dark-border bg-white dark:bg-dark-primary text-text-primary dark:text-dark-text placeholder-text-tertiary dark:placeholder-dark-textSecondary focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                        style={{ width: 'calc(100% - 0.75rem)' }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="pt-2">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full py-2.5 px-4 rounded-xl bg-primary text-white text-sm font-medium transition-all duration-200 hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Submitting...
              </>
            ) : (
              'Submit Feedback'
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default FeedbackModal;
